// Observes window size to provide current responsive breakpoint.
// Phase 0: Canonical API for Battlefield design boundaries (phone + tablet).
// src/hooks/ui/useBreakpoint.js
import { useState, useEffect } from 'react';

const breakpoints = {
  xs: 0,
  s: 576,
  m: 768,
  l: 992,
  xl: 1200,
};

/**
 * Converts width (px) to breakpoint token.
 * xs: 0–575, s: 576–767, m: 768–991, l: 992–1199, xl: 1200+
 */
export const getBreakpoint = (width) => {
  if (width < breakpoints.s) return 'xs';
  if (width < breakpoints.m) return 's';
  if (width < breakpoints.l) return 'm';
  if (width < breakpoints.xl) return 'l';
  return 'xl';
};

/**
 * Phase 0 design boundary predicates.
 * Canonical helper set for Phase 1+ to use instead of loose checks.
 */

/**
 * Phone: breakpoint < 768px (xs, s)
 * @param {string} breakpoint - from getBreakpoint() or useBreakpoint()
 * @returns {boolean}
 */
export const isPhoneBreakpoint = (breakpoint) => {
  return breakpoint === 'xs' || breakpoint === 's';
};

/**
 * Tablet: breakpoint 768–1199px (m, l)
 * @param {string} breakpoint
 * @returns {boolean}
 */
export const isTabletBreakpoint = (breakpoint) => {
  return breakpoint === 'm' || breakpoint === 'l';
};

/**
 * Battlefield: shared boundary until xl (1200px) = phone + tablet.
 * Covers all breakpoints except desktop (xl).
 * @param {string} breakpoint
 * @returns {boolean}
 */
export const isBattlefieldBreakpoint = (breakpoint) => {
  return isPhoneBreakpoint(breakpoint) || isTabletBreakpoint(breakpoint);
};

/**
 * Desktop: breakpoint >= 1200px (xl). Outside Battlefield.
 * @param {string} breakpoint
 * @returns {boolean}
 */
export const isDesktopBreakpoint = (breakpoint) => {
  return breakpoint === 'xl';
};

export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() =>
    getBreakpoint(window.innerWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      setBreakpoint(getBreakpoint(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

/**
 * Hook: returns device classification with current breakpoint.
 * Combines useBreakpoint() with design boundary predicates.
 * @returns {object} { breakpoint, isPhone, isTablet, isBattlefield, isDesktop }
 */
export const useDeviceClass = () => {
  const breakpoint = useBreakpoint();
  return {
    breakpoint,
    isPhone: isPhoneBreakpoint(breakpoint),
    isTablet: isTabletBreakpoint(breakpoint),
    isBattlefield: isBattlefieldBreakpoint(breakpoint),
    isDesktop: isDesktopBreakpoint(breakpoint),
  };
};

// Comparison object for ordering breakpoints
export const breakpointOrder = { xs: 0, s: 1, m: 2, l: 3, xl: 4 };
